import { sheets } from '@/app/grid/controller/Sheets';
import { content } from '@/app/gridGL/pixiApp/contentInstance';
import * as gridSettings from '@/app/gridGL/pixiApp/gridSettings';

/**
 * Code cells and formulas are not supported inside data tables. This guards the
 * various code-cell creation entry points (Insert menu, cell type menu, etc.) so
 * a user cannot create one on a cell that is inside a data table.
 *
 * Returns true (and shows a warning) when the cursor is inside a data table and
 * the caller should abort; returns false otherwise.
 */
export const preventCodeCellInTable = (): boolean => {
  const { x, y } = sheets.sheet.cursor.position;
  const codeCell = content.cellsSheet.tables.getCodeCellIntersects({ x, y });
  if (codeCell?.language === 'Import') {
    // Code is allowed in the data region of editable (import) tables (stored as
    // a single-cell CellValue::Code); only the table name and column-header
    // rows reject it.
    const isTableName = codeCell.show_name && y === codeCell.y;
    const isColumnHeader = codeCell.show_columns && y === codeCell.y + (codeCell.show_name ? 1 : 0);
    if (isTableName || isColumnHeader) {
      gridSettings.snackbar('Cannot create code cell inside table', { severity: 'error' });
      return true;
    }
  }
  return false;
};

export const openCodeEditor = async () => {
  const codeEditorState = gridSettings.getCodeEditorState();

  const { x, y } = sheets.sheet.cursor.position;
  const codeCell = content.cellsSheet.tables.getCodeCellIntersects({ x, y });
  if (codeCell) {
    const {
      codeCell: {
        pos: { x: openX, y: openY },
        language: openLanguage,
        sheetId: openSheetId,
      },
    } = codeEditorState;

    // check if the code editor is already open on the same cell
    const closeCodeEditor =
      codeEditorState.showCodeEditor &&
      openX === x &&
      openY === y &&
      openLanguage === codeCell.language &&
      openSheetId === sheets.current;

    if (closeCodeEditor) {
      // if the code editor is already open on the same cell, then close it
      // this open save changes modal if there are unsaved changes
      gridSettings.setCodeEditorState({
        ...codeEditorState,
        escapePressed: true,
      });
    } else if (codeCell.language === 'Import') {
      // A cell in the data region of an editable (import) table can hold a
      // single-cell code; open the cell type menu at the cursor so the user can
      // pick a language. The table name / column-header rows are rejected.
      const isTableName = codeCell.show_name && y === codeCell.y;
      const isColumnHeader = codeCell.show_columns && y === codeCell.y + (codeCell.show_name ? 1 : 0);
      if (isTableName || isColumnHeader) {
        gridSettings.snackbar('Cannot create code cell inside table', { severity: 'error' });
      } else {
        gridSettings.updateEditorInteractionState((prev) => ({
          ...prev,
          showCellTypeMenu: true,
        }));
        gridSettings.setCodeEditorState({
          ...codeEditorState,
          diffEditorContent: undefined,
          initialCode: '',
          codeCell: {
            sheetId: sheets.current,
            pos: { x, y },
            language: codeEditorState.codeCell.language,
            lastModified: 0,
          },
        });
      }
    } else {
      // if the code editor is not already open on the same cell, then open it
      // this will also open the save changes modal if there are unsaved changes
      // Check if it's a single-cell code cell (1x1 with no table UI)
      const isSingleCell = codeCell.w === 1 && codeCell.h === 1 && !codeCell.show_name && !codeCell.show_columns;
      gridSettings.setCodeEditorState({
        ...codeEditorState,
        diffEditorContent: undefined,
        waitingForEditorClose: {
          codeCell: {
            sheetId: sheets.current,
            pos: { x: codeCell.x, y: codeCell.y },
            language: codeCell.language,
            lastModified: Number(codeCell.last_modified),
            isSingleCell,
          },
          showCellTypeMenu: false,
          initialCode: '',
        },
      });
    }
  } else if (codeEditorState.showCodeEditor) {
    // code editor is already open, so check it for save before closing
    gridSettings.setCodeEditorState({
      ...codeEditorState,
      diffEditorContent: undefined,
      waitingForEditorClose: {
        codeCell: {
          sheetId: sheets.current,
          pos: { x, y },
          language: 'Python',
          lastModified: 0,
        },
        showCellTypeMenu: true,
        initialCode: '',
      },
    });
  } else {
    // just open the code editor selection menu
    gridSettings.updateEditorInteractionState((prev) => ({
      ...prev,
      showCellTypeMenu: true,
    }));
    gridSettings.setCodeEditorState({
      ...codeEditorState,
      diffEditorContent: undefined,
      initialCode: '',
      codeCell: {
        sheetId: sheets.current,
        pos: { x, y },
        language: codeEditorState.codeCell.language,
        lastModified: 0,
      },
    });
  }
};
